import { getMockExamApi, syncMockExamApi } from "@/api/services/admin/learning-content";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";

/**
 * Hook for syncing mock exam data from Google Spreadsheet.
 * After sync: refetches mock exam + question list, shows import stats toast.
 * If totalScoreSum != totalScore, shows score mismatch warning.
 */
export const useSyncMockExam = (industryId?: string, mockExamId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      if (!industryId || !mockExamId) return Promise.reject(new Error("Missing industryId or mockExamId"));
      return syncMockExamApi(industryId, mockExamId);
    },
    onSuccess: async (result) => {
      if (result.data.message) toast.success(result.data.message);

      // Invalidate question list
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_MOCK_EXAM_LIST, industryId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_MOCK_EXAM_QUESTION_LIST, industryId, mockExamId] });

      // Fetch fresh mock exam data directly (avoids cache timing issues with refetchQueries)
      try {
        const freshData = await getMockExamApi(industryId!, mockExamId!);
        // Update cache with fresh data
        queryClient.setQueryData([QUERY_KEYS.LEARNING_CONTENT.FETCH_MOCK_EXAM, industryId, mockExamId], freshData);

        if (freshData?.data) {
          const { totalScore, totalScoreSum } = freshData.data;
          if (totalScore != null && totalScoreSum !== totalScore) {
            toast.error(
              `試験情報の満点${totalScore}点に対して配点合計が${totalScoreSum}点です。試験情報またはシートを見直し、修正してから同期してください。`,
            );
          }
        }
      } catch {
        // Fallback: just invalidate to trigger background refetch
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_MOCK_EXAM, industryId, mockExamId] });
      }
    },
    onError: (error) => {
      showError(error);
    },
  });
};
